/**
 * Generic Game Coach Service
 * 
 * A shared AI coaching service that provides strategy advice, move analysis,
 * and educational explanations for any board/card game. Each game provides
 * its own context builder and local strategy knowledge.
 * 
 * Supports: Go, Backgammon, Checkers, Reversi, Mancala, Connect Four, 
 * Battleship, Rummy
 */

import { AWS_CONFIG } from '../aws/config';

export interface CoachMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface CoachAdvice {
  recommendation: string;
  confidence: 'low' | 'medium' | 'high';
  reasoning: string;
  tip?: string;
}

export interface GameContext {
  gameName: string;
  gameState: string;       // Text description of current state
  playerContext: string;   // What the player can see/do
  validMoves?: string;     // Available moves
  lastMove?: string;       // What just happened
}

export type GameKnowledge = {
  concepts: Record<string, string>;
  getAdvice: (context: GameContext) => CoachAdvice;
  getAnalysis: (context: GameContext) => string;
  answerQuestion: (question: string, context: GameContext) => string;
};

export class GameCoachService {
  private apiEndpoint: string;
  private useProxy: boolean;
  private apiKey: string;
  private conversationHistory: CoachMessage[] = [];
  private gameName: string;
  private knowledge: GameKnowledge;

  constructor(gameName: string, knowledge: GameKnowledge, apiKey?: string) {
    this.gameName = gameName;
    this.knowledge = knowledge;
    this.apiEndpoint = AWS_CONFIG.gemini.proxyEndpoint;
    this.useProxy = !!this.apiEndpoint;
    this.apiKey = apiKey || '';
  }

  async getAdvice(context: GameContext): Promise<CoachAdvice> {
    if (!this.useProxy && !this.apiKey) {
      return this.knowledge.getAdvice(context);
    }

    const prompt = `You are a ${this.gameName} coach advising a player.

${context.gameState}

${context.playerContext}

${context.validMoves ? `Available moves: ${context.validMoves}` : ''}

Provide strategic advice in JSON format:
{
  "recommendation": "what to do (specific move or action)",
  "confidence": "low" | "medium" | "high",
  "reasoning": "2-3 sentence explanation",
  "tip": "optional quick tip"
}`;

    try {
      const response = await this.callAPI(prompt);
      return this.parseAdvice(response);
    } catch {
      return this.knowledge.getAdvice(context);
    }
  }

  async analyzePosition(context: GameContext): Promise<string> {
    if (!this.useProxy && !this.apiKey) {
      return this.knowledge.getAnalysis(context);
    }

    const prompt = `You are a ${this.gameName} coach. Analyze this position in 3-4 sentences.

${context.gameState}

${context.playerContext}

Be specific and practical. Mention key features of the position.`;

    try {
      return await this.callAPI(prompt);
    } catch {
      return this.knowledge.getAnalysis(context);
    }
  }

  async askQuestion(question: string, context: GameContext): Promise<string> {
    if (!this.useProxy && !this.apiKey) {
      return this.knowledge.answerQuestion(question, context);
    }

    const prompt = `You are a friendly ${this.gameName} coach. The player asks: "${question}"

Current game situation:
${context.gameState}

${context.playerContext}

Give a clear, helpful answer in 2-4 sentences. Be specific to their situation.`;

    try {
      const response = await this.callAPI(prompt);
      this.conversationHistory.push({ role: 'user', content: question, timestamp: Date.now() });
      this.conversationHistory.push({ role: 'assistant', content: response, timestamp: Date.now() });
      if (this.conversationHistory.length > 20) {
        this.conversationHistory = this.conversationHistory.slice(-20);
      }
      return response;
    } catch {
      return this.knowledge.answerQuestion(question, context);
    }
  }

  async explainConcept(concept: string): Promise<string> {
    const local = this.knowledge.concepts[concept.toLowerCase()];
    if (local) return local;

    if (!this.useProxy && !this.apiKey) {
      return `${concept} is an important concept in ${this.gameName}. Understanding it will improve your gameplay.`;
    }

    const prompt = `Explain "${concept}" in ${this.gameName} in 3-4 simple sentences for a beginner.`;
    try {
      return await this.callAPI(prompt);
    } catch {
      return `${concept} is an important concept in ${this.gameName}.`;
    }
  }

  getHistory(): CoachMessage[] {
    return [...this.conversationHistory];
  }

  clearHistory(): void {
    this.conversationHistory = [];
  }

  private async callAPI(prompt: string): Promise<string> {
    if (this.useProxy) {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, config: { temperature: 0.7, maxOutputTokens: 500 } })
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.text || '';
    } else if (this.apiKey) {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 500 }
          })
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
    }
    throw new Error('No API configured');
  }

  private parseAdvice(response: string): CoachAdvice {
    try {
      const match = response.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        return {
          recommendation: parsed.recommendation || 'Consider your options.',
          confidence: parsed.confidence || 'medium',
          reasoning: parsed.reasoning || 'Think about your position.',
          tip: parsed.tip
        };
      }
    } catch { /* fall through */ }
    return { recommendation: 'Think carefully.', confidence: 'low', reasoning: response.slice(0, 200) };
  }
}
